import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { buildApiUrl } from '../config/api';

interface AnalysisFeedbackProps {
  sessionId?: string;
  onFeedbackSubmitted?: () => void;
}

export const AnalysisFeedback: React.FC<AnalysisFeedbackProps> = ({
  sessionId,
  onFeedbackSubmitted
}) => {
  const { token } = useAuth();
  const [showFeedback, setShowFeedback] = useState(true);
  const [rating, setRating] = useState(0);
  const [wasHelpful, setWasHelpful] = useState<boolean | null>(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [improvementNotes, setImprovementNotes] = useState('');
  const [actionTaken, setActionTaken] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const quickFeedbackOptions = [
    "Action wasn't specific enough",
    "Too much analysis, not enough action",
    "Didn't understand my goal",
    "Perfect - exactly what I needed!",
    "Good but needs clearer next steps",
    "Analysis was too generic"
  ];

  const handleSubmitFeedback = async () => {
    if (!sessionId || !token) return;

    setIsSubmitting(true);

    try {
      const response = await fetch(buildApiUrl('/ai/feedback'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId,
          rating,
          wasHelpful,
          feedbackText,
          improvementNotes,
          actionTaken,
        }),
      });

      if (response.ok) {
        setSubmitted(true);
        setTimeout(() => {
          setShowFeedback(false);
          onFeedbackSubmitted?.();
        }, 2000);
      }
    } catch (error) {
      console.error('Failed to submit feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!showFeedback || !sessionId) return null;

  if (submitted) {
    return (
      <div className="mt-6 p-6 bg-gradient-to-r from-green-50 to-teal-50 rounded-lg border border-green-300">
        <div className="text-center">
          <div className="text-3xl mb-2">✅</div>
          <div className="text-lg font-bold text-green-700">Thanks for your feedback!</div>
          <div className="text-sm text-gray-600">This helps improve GoalYet's AI</div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6 p-6 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-lg border border-orange-300">
      <div className="flex justify-between items-start mb-4">
        <h3 className="text-lg font-bold text-gray-800">
          🔄 Quick Feedback (Development Mode)
        </h3>
        <button
          onClick={() => setShowFeedback(false)}
          className="text-gray-500 hover:text-gray-700"
        >
          ×
        </button>
      </div>

      {/* Quick Rating */}
      <div className="mb-4">
        <div className="flex items-center gap-4 mb-3">
          <span className="text-sm font-medium text-gray-700">Was this helpful?</span>
          <div className="flex gap-2">
            <button
              onClick={() => setWasHelpful(true)}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                wasHelpful === true
                  ? 'bg-green-500 text-white'
                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
              }`}
            >
              👍 Yes
            </button>
            <button
              onClick={() => setWasHelpful(false)}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                wasHelpful === false
                  ? 'bg-red-500 text-white'
                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
              }`}
            >
              👎 No
            </button>
          </div>
        </div>

        {/* Star Rating */}
        <div className="flex items-center gap-4">
          <span className="text-sm font-medium text-gray-700">Rate the analysis:</span>
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                onClick={() => setRating(star)}
                className={`text-2xl transition-all hover:scale-110 ${
                  star <= rating ? 'text-orange-500' : 'text-gray-300'
                }`}
              >
                ⭐
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Did you take action? */}
      <div className="mb-4">
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <input
            type="checkbox"
            checked={actionTaken}
            onChange={(e) => setActionTaken(e.target.checked)}
            className="rounded text-orange-500"
          />
          I actually did the suggested action!
        </label>
      </div>

      {/* Quick Feedback Options */}
      <div className="mb-4">
        <div className="text-sm font-medium text-gray-700 mb-2">Quick feedback:</div>
        <div className="flex flex-wrap gap-2">
          {quickFeedbackOptions.map((option) => (
            <button
              key={option}
              onClick={() => setFeedbackText(option)}
              className={`px-3 py-1 rounded-lg text-sm transition-all ${
                feedbackText === option
                  ? 'bg-orange-500 text-white'
                  : 'bg-white border border-gray-300 text-gray-600 hover:border-orange-400'
              }`}
            >
              {option}
            </button>
          ))}
        </div>
      </div>

      {/* Improvement Suggestions */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          How could the analysis be better?
        </label>
        <textarea
          value={improvementNotes}
          onChange={(e) => setImprovementNotes(e.target.value)}
          placeholder="e.g., 'Make the action even more specific - tell me which file to open'"
          className="w-full p-3 rounded-lg border border-gray-300 focus:border-orange-500 focus:outline-none resize-none h-20"
        />
      </div>

      {/* Submit Button */}
      <button
        onClick={handleSubmitFeedback}
        disabled={isSubmitting || (!rating && wasHelpful === null)}
        className={`w-full px-4 py-3 rounded-lg font-bold transition-all ${
          isSubmitting || (!rating && wasHelpful === null)
            ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
            : 'bg-gradient-to-r from-orange-500 to-teal-500 text-white hover:shadow-lg'
        }`}
      >
        {isSubmitting ? 'Submitting...' : 'Submit Feedback & Continue'}
      </button>

      <div className="mt-3 text-xs text-gray-500 text-center">
        This feedback helps train better prompts for GoalYet
      </div>
    </div>
  );
};